/**
 * Modal para ver detalles de un Miembro
 */

import { Button, Badge, RoleBadge } from "@/components/ui";
import {
  X,
  Edit,
  User,
  Mail,
  Calendar,
  Building2,
  Users,
  Award,
  Phone,
  MapPin,
  Heart,
  Shield,
  FileText,
  AlertCircle,
} from "lucide-react";
import type { MemberWithPerson } from "@/types";
import {
  MEMBER_CLASS_LABELS,
  ID_DOCUMENT_TYPE_LABELS,
  BLOOD_TYPE_LABELS,
  GENDER_LABELS,
} from "@/types";

interface MemberDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  member: MemberWithPerson;
  onEdit: () => void;
}

const statusLabels: Record<string, string> = {
  active: "Activo",
  inactive: "Inactivo",
  pending: "Pendiente",
  transferred: "Transferido",
};

const statusColors: Record<string, string> = {
  active: "bg-green-100 text-green-800",
  inactive: "bg-gray-100 text-gray-800",
  pending: "bg-yellow-100 text-yellow-800",
  transferred: "bg-blue-100 text-blue-800",
};

const clubTypeLabels: Record<string, string> = {
  aventureros: "Aventureros",
  conquistadores: "Conquistadores",
  guias_mayores: "Guías Mayores",
};

export default function MemberDetailModal({
  isOpen,
  onClose,
  member,
  onEdit,
}: MemberDetailModalProps) {
  if (!isOpen) return null;

  const calculateAge = (
    birthDate: { toDate?: () => Date } | Date | null | undefined,
  ) => {
    if (!birthDate) return "-";
    const date =
      typeof birthDate === "object" && "toDate" in birthDate && birthDate.toDate
        ? birthDate.toDate()
        : new Date(birthDate as unknown as string | number | Date);
    const today = new Date();
    let age = today.getFullYear() - date.getFullYear();
    const monthDiff = today.getMonth() - date.getMonth();
    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < date.getDate())
    ) {
      age--;
    }
    return `${age} años`;
  };

  const formatDate = (
    timestamp: { toDate?: () => Date } | Date | null | undefined,
  ) => {
    if (!timestamp) return "-";
    const date =
      typeof timestamp === "object" && "toDate" in timestamp && timestamp.toDate
        ? timestamp.toDate()
        : new Date(timestamp as unknown as string | number | Date);
    return date.toLocaleDateString("es-CO", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-2xl animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-gray-50 to-white border-b border-gray-100 px-6 py-4 flex items-center justify-between rounded-t-2xl">
          <div className="flex items-center gap-3">
            {member.person.photoUrl ? (
              <img
                src={member.person.photoUrl}
                alt={member.person.fullName}
                className="w-12 h-12 rounded-xl object-cover shadow-md ring-2 ring-white"
              />
            ) : (
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center text-white text-lg font-bold shadow-md shadow-primary-500/25">
                {member.person.fullName?.charAt(0) || "?"}
              </div>
            )}
            <div>
              <h2 className="text-lg font-bold text-gray-900">
                {member.person.fullName}
              </h2>
              <RoleBadge
                role={member.role}
                section={member.section}
                showSection
                size="sm"
              />
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-xl transition-colors duration-200"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Estado */}
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-500">Estado</span>
            <Badge className={statusColors[member.status]}>
              {statusLabels[member.status] || member.status}
            </Badge>
          </div>

          {/* Información Personal */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider flex items-center gap-2">
              <div className="w-6 h-6 bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl flex items-center justify-center shadow-sm shadow-blue-500/25">
                <User className="w-3.5 h-3.5 text-white" />
              </div>
              Información Personal
            </h3>

            <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-4 space-y-3 border-2 border-gray-100">
              <div>
                <p className="text-xs text-gray-500">Género</p>
                <p className="text-sm text-gray-900">
                  {member.person.gender
                    ? GENDER_LABELS[member.person.gender]
                    : "-"}
                </p>
              </div>

              <div className="flex items-center gap-3">
                <FileText className="w-4 h-4 text-gray-400" />
                <div>
                  <p className="text-xs text-gray-500">Identificación</p>
                  <p className="text-sm text-gray-900">
                    {member.person.idDocumentType
                      ? ID_DOCUMENT_TYPE_LABELS[member.person.idDocumentType]
                      : "Doc"}
                    : {member.person.idDocumentNumber || "-"}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Calendar className="w-4 h-4 text-gray-400" />
                <div>
                  <p className="text-xs text-gray-500">Fecha de Nacimiento</p>
                  <p className="text-sm text-gray-900">
                    {formatDate(member.person.birthDate)} (
                    {calculateAge(member.person.birthDate)})
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Heart className="w-4 h-4 text-gray-400" />
                <div>
                  <p className="text-xs text-gray-500">Tipo de Sangre</p>
                  <p className="text-sm text-gray-900 font-medium">
                    {member.person.bloodType
                      ? BLOOD_TYPE_LABELS[member.person.bloodType]
                      : "-"}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Phone className="w-4 h-4 text-gray-400" />
                <div>
                  <p className="text-xs text-gray-500">Teléfono</p>
                  <p className="text-sm text-gray-900">
                    {member.person.phone || "-"}
                  </p>
                </div>
              </div>

              {member.person.email && (
                <div className="flex items-center gap-3">
                  <Mail className="w-4 h-4 text-gray-400" />
                  <div>
                    <p className="text-xs text-gray-500">Email</p>
                    <p className="text-sm text-gray-900">
                      {member.person.email}
                    </p>
                  </div>
                </div>
              )}

              {member.person.healthInsurance && (
                <div className="flex items-center gap-3">
                  <Shield className="w-4 h-4 text-gray-400" />
                  <div>
                    <p className="text-xs text-gray-500">Sisbén / EPS</p>
                    <p className="text-sm text-gray-900">
                      {member.person.healthInsurance}
                    </p>
                  </div>
                </div>
              )}

              {member.person.linkedUserId && (
                <div className="flex items-center gap-2 text-sm text-green-600">
                  <div className="w-2 h-2 bg-green-500 rounded-full" />
                  Cuenta vinculada
                </div>
              )}
            </div>
          </div>

          {/* Información de Contacto */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider flex items-center gap-2">
              <div className="w-6 h-6 bg-gradient-to-br from-red-400 to-red-600 rounded-xl flex items-center justify-center shadow-sm shadow-red-500/25">
                <AlertCircle className="w-3.5 h-3.5 text-white" />
              </div>
              Contacto de Emergencia
            </h3>

            <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-4 space-y-3 border-2 border-gray-100">
              {member.person.address && (
                <div className="flex items-center gap-3">
                  <MapPin className="w-4 h-4 text-gray-400" />
                  <div>
                    <p className="text-xs text-gray-500">Dirección</p>
                    <p className="text-sm text-gray-900">
                      {member.person.address}
                    </p>
                  </div>
                </div>
              )}

              {member.person.emergencyContact && (
                <>
                  <div className="flex items-center gap-3">
                    <User className="w-4 h-4 text-gray-400" />
                    <div>
                      <p className="text-xs text-gray-500">
                        Persona de Contacto
                      </p>
                      <p className="text-sm text-gray-900">
                        {member.person.emergencyContact.name} (
                        {member.person.emergencyContact.relationship})
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Phone className="w-4 h-4 text-gray-400" />
                    <div>
                      <p className="text-xs text-gray-500">
                        Teléfono de Emergencia
                      </p>
                      <p className="text-sm text-gray-900">
                        {member.person.emergencyContact.phone}
                      </p>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Información de Membresía */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider flex items-center gap-2">
              <div className="w-6 h-6 bg-gradient-to-br from-green-400 to-green-600 rounded-xl flex items-center justify-center shadow-sm shadow-green-500/25">
                <Building2 className="w-3.5 h-3.5 text-white" />
              </div>
              Membresía
            </h3>

            <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-4 space-y-3 border-2 border-gray-100">
              <div className="flex items-center gap-3">
                <Users className="w-4 h-4 text-gray-400" />
                <div>
                  <p className="text-xs text-gray-500">Sección</p>
                  <p className="text-sm text-gray-900">
                    {clubTypeLabels[
                      member.section || member.clubType || "conquistadores"
                    ] ||
                      member.section ||
                      member.clubType}
                  </p>
                </div>
              </div>

              {member.clase && (
                <div className="flex items-center gap-3">
                  <Award className="w-4 h-4 text-gray-400" />
                  <div>
                    <p className="text-xs text-gray-500">Clase/Nivel</p>
                    <p className="text-sm text-gray-900">
                      {MEMBER_CLASS_LABELS[member.clase] || member.clase}
                    </p>
                  </div>
                </div>
              )}

              <div className="flex items-center gap-3">
                <Calendar className="w-4 h-4 text-gray-400" />
                <div>
                  <p className="text-xs text-gray-500">Fecha de Inscripción</p>
                  <p className="text-sm text-gray-900">
                    {formatDate(member.registrationDate)}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500">
                  Inscripción pagada:
                </span>
                {member.registrationPaid ? (
                  <Badge className="bg-green-100 text-green-800">Sí</Badge>
                ) : (
                  <Badge className="bg-red-100 text-red-800">No</Badge>
                )}
              </div>
            </div>
          </div>

          {/* Historial de roles */}
          {member.roleHistory && member.roleHistory.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">
                Historial de Cargos
              </h3>

              <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-4 space-y-2 border-2 border-gray-100">
                {member.roleHistory.map((history, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between text-sm p-2 rounded-xl hover:bg-gray-100 transition-colors duration-200"
                  >
                    <RoleBadge role={history.role} size="xs" variant="soft" />
                    <span className="text-gray-500">{history.period}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="sticky bottom-0 bg-gradient-to-r from-gray-50 to-white border-t border-gray-100 px-6 py-4 flex justify-end gap-3 rounded-b-2xl">
          <Button variant="secondary" onClick={onClose} className="shadow-sm">
            Cerrar
          </Button>
          <Button
            onClick={onEdit}
            className="flex items-center gap-2 bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 shadow-lg shadow-primary-500/25 transition-all duration-300"
          >
            <Edit className="w-4 h-4" />
            Editar
          </Button>
        </div>
      </div>
    </div>
  );
}
